import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Building2, MapPin, QrCode, Download, FileSpreadsheet, Loader2 } from "lucide-react";

const PERIODS = [
  { v: "today", l: "Hoy" },
  { v: "7d", l: "Últimos 7 días" },
  { v: "30d", l: "Últimos 30 días" },
  { v: "month", l: "Este mes" },
  { v: "year", l: "Último año" },
  { v: "ytd", l: "Este año" },
  { v: "custom", l: "Personalizado" },
];

const SCOPES = [
  { v: "client", l: "Por cliente", icon: Building2 },
  { v: "location", l: "Por sucursal", icon: MapPin },
  { v: "area", l: "Por área", icon: QrCode },
];

function colorFor(p) {
  if (p === null || p === undefined) return "#94A3B8";
  if (p >= 85) return "#10B981";
  if (p >= 60) return "#F59E0B";
  return "#EF4444";
}

function ProgressBar({ value }) {
  const v = value === null || value === undefined ? 0 : Math.min(value, 100);
  const color = colorFor(value);
  return (
    <div className="w-full">
      <div className="h-2 rounded-full bg-lx-surface overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: color }}/>
      </div>
    </div>
  );
}

export default function CompliancePage() {
  const { user } = useAuth();
  const isClient = user?.role === "cliente";
  const [period, setPeriod] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [scope, setScope] = useState(isClient ? "location" : "client");
  const [filterClient, setFilterClient] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [c, l] = await Promise.all([api.get("/clients"), api.get("/locations")]);
      setClients(c.data); setLocations(l.data);
    })();
  }, []);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    let stale = false;
    (async () => {
      setLoading(true);
      const params = { period, scope };
      if (period === "custom") { params.start = customStart; params.end = customEnd; }
      if (filterClient) params.client_id = filterClient;
      if (filterLocation) params.location_id = filterLocation;
      const { data } = await api.get("/dashboard/compliance", { params });
      if (stale) return;
      setData(data);
      setLoading(false);
    })();
    return () => { stale = true; };
  }, [period, scope, filterClient, filterLocation, customStart, customEnd]);

  const filteredLocations = useMemo(
    () => filterClient ? locations.filter(l => l.client_id === filterClient) : locations,
    [locations, filterClient]
  );

  const exportCSV = () => {
    if (!data?.items?.length) return;
    const head = scope === "area"
      ? ["Área", "Sucursal", "Cliente", "Esperado", "Realizado", "Cumplimiento %"]
      : ["Nombre", "Áreas", "Esperado", "Realizado", "Cumplimiento %"];
    const rows = data.items.map(it => scope === "area"
      ? [it.area_name, it.location_name || "", it.client_name || "", it.expected, it.actual, it.percentage ?? ""]
      : [it.name, it.areas, it.expected, it.actual, it.percentage ?? ""]
    );
    const csv = [head, ...rows].map(r => r.map(x => `"${String(x ?? "").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `cumplimiento-${scope}-${period}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState("");
  const downloadExcel = async () => {
    setExcelLoading(true); setExcelError("");
    try {
      const params = { period };
      if (period === "custom") { params.start = customStart; params.end = customEnd; }
      if (filterClient) params.client_id = filterClient;
      if (filterLocation) params.location_id = filterLocation;
      const res = await api.get("/reports/excel", { params, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "reporte-limpio-express.xlsx";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExcelError("No se pudo generar el reporte. Intenta de nuevo.");
    } finally {
      setExcelLoading(false);
    }
  };

  const overall = data?.overall;
  const periodLabel = PERIODS.find(p => p.v === period)?.l || "Hoy";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Reportes</div>
          <h1 className="lx-section-title mt-1">Cumplimiento</h1>
          <p className="text-lx-muted text-sm mt-1">Detalle de % de cumplimiento por cliente, sucursal o área.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="lx-btn lx-btn-primary h-[42px]" onClick={downloadExcel}
            disabled={excelLoading || (period === "custom" && (!customStart || !customEnd))} data-testid="export-report-excel">
            {excelLoading ? <Loader2 size={16} className="animate-spin"/> : <FileSpreadsheet size={16}/>}
            {excelLoading ? "Generando…" : "Descargar reporte Excel"}
          </button>
          <button className="lx-btn lx-btn-secondary h-[42px]" onClick={exportCSV} disabled={!data?.items?.length} data-testid="export-compliance-csv">
            <Download size={16}/> Exportar CSV
          </button>
          {excelError && <div className="w-full text-sm text-red-600" data-testid="export-report-error">{excelError}</div>}
        </div>
      </div>

      {/* Filters */}
      <div className="lx-card space-y-4">
        <div>
          <div className="lx-overline mb-2">Período</div>
          <div className="flex flex-wrap gap-2">
            {PERIODS.map(p => (
              <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`compliance-period-${p.v}`}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition ${period === p.v ? "bg-lx-primary text-white border-lx-primary" : "bg-white text-lx-muted border-lx-border hover:border-lx-primary/40"}`}>
                {p.l}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex flex-wrap items-end gap-3 p-3 mt-3 rounded-lg bg-lx-surface border border-lx-border" data-testid="compliance-custom-pickers">
              <div>
                <label className="lx-label">Desde</label>
                <input type="date" className="lx-input" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} data-testid="compliance-custom-start"/>
              </div>
              <div>
                <label className="lx-label">Hasta</label>
                <input type="date" className="lx-input" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} data-testid="compliance-custom-end"/>
              </div>
              {(!customStart || !customEnd) && (
                <span className="text-xs text-lx-muted pb-2.5">Selecciona ambas fechas para ver el reporte.</span>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="lx-overline mb-2">Agrupar</div>
          <div className="flex flex-wrap gap-2">
            {SCOPES.filter(s => !isClient || s.v !== "client").map(s => (
              <button key={s.v} onClick={() => setScope(s.v)} data-testid={`compliance-scope-${s.v}`}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition ${scope === s.v ? "bg-lx-primary text-white border-lx-primary" : "bg-white text-lx-muted border-lx-border hover:border-lx-primary/40"}`}>
                <s.icon size={14}/> {s.l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {!isClient && (
            <div>
              <label className="lx-label">Filtrar por cliente</label>
              <select className="lx-input" value={filterClient} onChange={(e) => { setFilterClient(e.target.value); setFilterLocation(""); }} data-testid="compliance-filter-client">
                <option value="">Todos</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="lx-label">Filtrar por sucursal</label>
            <select className="lx-input" value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} data-testid="compliance-filter-location">
              <option value="">Todas</option>
              {filteredLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Overall summary */}
      {overall && (
        <div className="lx-card" data-testid="overall-summary">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="lx-overline">Cumplimiento global · {periodLabel.toLowerCase()}</div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="font-heading font-bold text-5xl tracking-tight" style={{ color: colorFor(overall.percentage) }} data-testid="overall-percentage">
                  {overall.percentage === null ? "—" : `${Math.round(overall.percentage)}%`}
                </span>
                <span className="text-sm text-lx-muted">
                  {overall.actual} de {overall.expected} limpiezas válidas · {overall.areas} áreas
                </span>
              </div>
            </div>
            <ProgressBar value={overall.percentage}/>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> :
         !data?.items?.length ? <div className="p-10 text-center text-lx-muted">Sin datos para los filtros seleccionados.</div> : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              {scope === "area" ? (
                <>
                  <thead>
                    <tr>
                      <th>Área</th><th>Sucursal</th><th>Cliente</th>
                      <th className="text-right">Esperado</th>
                      <th className="text-right">Realizado</th>
                      <th style={{ minWidth: 220 }}>Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it, idx) => (
                      <tr key={it.area_id || `area-${idx}`} data-testid={`compliance-row-${it.area_id || idx}`}>
                        <td>
                          <div className="font-medium">{it.area_name}</div>
                          {it.area_type && <div className="text-xs text-lx-muted">{it.area_type}</div>}
                        </td>
                        <td>{it.location_name || "—"}</td>
                        <td>{it.client_name || "—"}</td>
                        <td className="text-right tabular-nums">{it.expected}</td>
                        <td className="text-right tabular-nums font-medium">{it.actual}</td>
                        <td>
                          <div className="flex items-center gap-3">
                            <ProgressBar value={it.percentage}/>
                            <span className="text-sm font-semibold tabular-nums w-12 text-right" style={{ color: colorFor(it.percentage) }}>
                              {it.percentage === null ? "—" : `${Math.round(it.percentage)}%`}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th>{scope === "client" ? "Cliente" : "Sucursal"}</th>
                      <th className="text-right">Áreas</th>
                      <th className="text-right">Esperado</th>
                      <th className="text-right">Realizado</th>
                      <th style={{ minWidth: 220 }}>Cumplimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((it, idx) => (
                      <tr key={it.id || `row-${idx}`} data-testid={`compliance-row-${it.id || idx}`}>
                        <td className="font-medium">{it.name}</td>
                        <td className="text-right tabular-nums">{it.areas}</td>
                        <td className="text-right tabular-nums">{it.expected}</td>
                        <td className="text-right tabular-nums font-medium">{it.actual}</td>
                        <td>
                          <div className="flex items-center gap-3">
                            <ProgressBar value={it.percentage}/>
                            <span className="text-sm font-semibold tabular-nums w-12 text-right" style={{ color: colorFor(it.percentage) }}>
                              {it.percentage === null ? "—" : `${Math.round(it.percentage)}%`}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
